import { io } from 'socket.io-client';
import { BASE_API, TICKETING_API } from './consts';

// Unified Socket Client for both HRM and Ticketing platforms
class UnifiedSocketClient {
  constructor() {
    this.hrmSocket = null;
    this.ticketingSocket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 5000;
    this.heartbeatInterval = 60000;
    this.heartbeatTimer = null;
    
    // Event handlers
    this.eventHandlers = new Map();
    this.connectionHandlers = new Set();
    this.disconnectionHandlers = new Set();
    this.errorHandlers = new Set();
  }

  // Get authentication token from localStorage or cookies
  getAuthToken() {
    // Check if we're in browser environment
    if (typeof window === 'undefined') {
      console.log('⚠️ [UNIFIED SOCKET] Not in browser environment');
      return null;
    }

    // Try localStorage first (HRM app uses this)
    let token = localStorage.getItem('token');
    if (token) {
      console.log('✅ [UNIFIED SOCKET] Token found in localStorage');
      return token;
    }

    // Try cookies (Ticketing app uses this)
    token = this.getCookie('hrm_auth_token');
    if (token) {
      console.log('✅ [UNIFIED SOCKET] Token found in cookies');
      return token;
    }

    console.log('❌ [UNIFIED SOCKET] No token found in localStorage or cookies');
    return null;
  }

  // Helper to get cookie value
  getCookie(name) {
    if (typeof document === 'undefined') return null;
    
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift() || null;
    }
    return null;
  }

  // Connect to both platforms
  async connect() {
    try {
      console.log('🔌 [UNIFIED SOCKET] Connecting to both platforms...');
      
      const token = this.getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Connect to HRM platform
      await this.connectToHRM(token);
      
      // Connect to Ticketing platform
      await this.connectToTicketing(token);
      
      this.isConnected = true;
      this.startHeartbeat();
      
      console.log('✅ [UNIFIED SOCKET] Connected to both platforms successfully');
      return true;
    } catch (error) {
      console.error('❌ [UNIFIED SOCKET] Connection failed:', error);
      this.handleError(error);
      return false;
    }
  }

  // Connect to HRM Socket.IO server
  async connectToHRM(token) {
    return new Promise((resolve, reject) => {
      try {
        console.log('🔌 [UNIFIED SOCKET] Connecting to HRM platform...');
        
        // Remove /api suffix if present (Socket.IO connects to root, not /api endpoint)
        // BASE_API might be "http://localhost:3001/api" but Socket.IO should connect to "http://localhost:3001"
        const hrmSocketUrl = BASE_API.replace(/\/api\/?$/, '');
        console.log('🔌 [UNIFIED SOCKET] HRM Socket URL:', hrmSocketUrl);
        
        this.hrmSocket = io(hrmSocketUrl, {
          path: '/socket.io',
          auth: { token },
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: this.reconnectInterval,
          timeout: 10000
        });

        this.hrmSocket.on('connect', () => {
          console.log('✅ [UNIFIED SOCKET] HRM platform connected');
          this.setupHRMEventHandlers();
          resolve();
        });

        this.hrmSocket.on('connect_error', (error) => {
          console.error('❌ [UNIFIED SOCKET] HRM connection error:', error);
          reject(error);
        });

        this.hrmSocket.on('disconnect', (reason) => {
          console.log('🔌 [UNIFIED SOCKET] HRM platform disconnected:', reason);
          this.handleDisconnection('hrm', reason);
        });

      } catch (error) {
        reject(error);
      }
    });
  }

  // Connect to Ticketing Socket.IO server
  async connectToTicketing(token) {
    return new Promise((resolve, reject) => {
      try {
        console.log('🔌 [UNIFIED SOCKET] Connecting to Ticketing platform...');
        
        // Remove /api suffix if present (Socket.IO connects to root, not /api endpoint)
        const ticketingSocketUrl = TICKETING_API.replace(/\/api\/?$/, '');
        console.log('🔌 [UNIFIED SOCKET] Ticketing Socket URL:', ticketingSocketUrl);
        
        this.ticketingSocket = io(ticketingSocketUrl, {
          path: '/socket.io',
          auth: { token },
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: this.reconnectInterval,
          timeout: 10000
        });

        this.ticketingSocket.on('connect', () => {
          console.log('✅ [UNIFIED SOCKET] Ticketing platform connected');
          this.setupTicketingEventHandlers();
          resolve();
        });

        this.ticketingSocket.on('connect_error', (error) => {
          console.error('❌ [UNIFIED SOCKET] Ticketing connection error:', error);
          reject(error);
        });

        this.ticketingSocket.on('disconnect', (reason) => {
          console.log('🔌 [UNIFIED SOCKET] Ticketing platform disconnected:', reason);
          this.handleDisconnection('ticketing', reason);
        });

      } catch (error) {
        reject(error);
      }
    });
  }

  // Set up HRM event handlers
  setupHRMEventHandlers() {
    if (!this.hrmSocket) return;

    // Connection established
    this.hrmSocket.on('connection_established', (data) => {
      console.log('🔗 [UNIFIED SOCKET] HRM connection established:', data);
      this.emit('connection_established', { platform: 'hrm', ...data });
    });

    // Notifications
    this.hrmSocket.on('notification', (data) => {
      console.log('🔔 [UNIFIED SOCKET] HRM notification received:', data);
      this.emit('notification', { platform: 'hrm', data });
    });

    // Broadcast messages
    this.hrmSocket.on('broadcast', (data) => {
      console.log('📢 [UNIFIED SOCKET] HRM broadcast received:', data);
      this.emit('broadcast', { platform: 'hrm', data });
    });

    // Time tracking events
    this.hrmSocket.on('timeTrackingStatusUpdated', (data) => {
      console.log('⏰ [UNIFIED SOCKET] HRM time tracking update:', data);
      this.emit('timeTrackingStatusUpdated', { platform: 'hrm', data });
    });

    // Pong response
    this.hrmSocket.on('pong', (data) => {
      console.log('🏓 [UNIFIED SOCKET] HRM pong received:', data);
    });

    // Stats
    this.hrmSocket.on('stats', (data) => {
      console.log('📊 [UNIFIED SOCKET] HRM stats received:', data);
      this.emit('stats', { platform: 'hrm', data });
    });

    // Subscribe to default channels
    this.hrmSocket.emit('subscribe', { 
      channels: ['notifications', 'general', 'time_tracking'] 
    });
  }

  // Set up Ticketing event handlers
  setupTicketingEventHandlers() {
    if (!this.ticketingSocket) return;

    // Ticket events
    this.ticketingSocket.on('ticket-created', (data) => {
      console.log('🎫 [UNIFIED SOCKET] Ticket created:', data);
      this.emit('ticket-created', { platform: 'ticketing', data });
    });

    this.ticketingSocket.on('ticket-updated', (data) => {
      console.log('🎫 [UNIFIED SOCKET] Ticket updated:', data);
      this.emit('ticket-updated', { platform: 'ticketing', data });
    });

    this.ticketingSocket.on('ticket-status-changed', (data) => {
      console.log('🎫 [UNIFIED SOCKET] Ticket status changed:', data);
      this.emit('ticket-status-changed', { platform: 'ticketing', data });
    });

    this.ticketingSocket.on('ticket-deleted', (data) => {
      console.log('🎫 [UNIFIED SOCKET] Ticket deleted:', data);
      this.emit('ticket-deleted', { platform: 'ticketing', data });
    });

    // Payroll ticket events
    this.ticketingSocket.on('payrollTicketCreated', (data) => {
      console.log('💰 [UNIFIED SOCKET] Payroll ticket created:', data);
      this.emit('payrollTicketCreated', { platform: 'ticketing', data });
    });

    this.ticketingSocket.on('payrollTicketUpdated', (data) => {
      console.log('💰 [UNIFIED SOCKET] Payroll ticket updated:', data);
      this.emit('payrollTicketUpdated', { platform: 'ticketing', data });
    });

    this.ticketingSocket.on('payrollTicketStatusChanged', (data) => {
      console.log('💰 [UNIFIED SOCKET] Payroll ticket status changed:', data);
      this.emit('payrollTicketStatusChanged', { platform: 'ticketing', data });
    });

    // Overtime ticket events
    this.ticketingSocket.on('overtimeTicketCreated', (data) => {
      console.log('⏰ [UNIFIED SOCKET] Overtime ticket created:', data);
      this.emit('overtimeTicketCreated', { platform: 'ticketing', data });
    });

    this.ticketingSocket.on('overtimeTicketUpdated', (data) => {
      console.log('⏰ [UNIFIED SOCKET] Overtime ticket updated:', data);
      this.emit('overtimeTicketUpdated', { platform: 'ticketing', data });
    });

    this.ticketingSocket.on('overtimeTicketStatusChanged', (data) => {
      console.log('⏰ [UNIFIED SOCKET] Overtime ticket status changed:', data);
      this.emit('overtimeTicketStatusChanged', { platform: 'ticketing', data });
    });
    this.ticketingSocket.on('notification', (data) => {
      console.log('⏰ [UNIFIED SOCKET] Ticketing time tracking update:', data);
      this.emit('notification', { platform: 'ticketing', data });
    });
    // Time tracking events
    this.ticketingSocket.on('timeTrackingStatusUpdated', (data) => {
      console.log('⏰ [UNIFIED SOCKET] Ticketing time tracking update:', data);
      this.emit('timeTrackingStatusUpdated', { platform: 'ticketing', data });
    });
  }

  // Disconnect from both platforms
  disconnect() {
    console.log('🔌 [UNIFIED SOCKET] Disconnecting from both platforms...');
    
    this.isConnected = false;
    this.stopHeartbeat();

    if (this.hrmSocket) {
      this.hrmSocket.disconnect();
      this.hrmSocket = null;
    }

    if (this.ticketingSocket) {
      this.ticketingSocket.disconnect();
      this.ticketingSocket = null;
    }

    console.log('🔌 [UNIFIED SOCKET] Disconnected from both platforms');
  }

  // Send message to specific platform
  send(platform, event, data) {
    const socket = platform === 'hrm' ? this.hrmSocket : this.ticketingSocket;
    
    if (socket && socket.connected) {
      socket.emit(event, data);
      console.log(`📤 [UNIFIED SOCKET] Sent ${event} to ${platform}:`, data);
      return true;
    } else {
      console.warn(`⚠️ [UNIFIED SOCKET] Cannot send to ${platform}: not connected`);
      return false;
    }
  }

  // Subscribe to channels on specific platform
  subscribe(platform, channels) {
    return this.send(platform, 'subscribe', { channels });
  }

  // Unsubscribe from channels on specific platform
  unsubscribe(platform, channels) {
    return this.send(platform, 'unsubscribe', { channels });
  }

  // Ping specific platform
  ping(platform) {
    return this.send(platform, 'ping');
  }

  // Add event listener
  on(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event).add(handler);
    
    return () => {
      const handlers = this.eventHandlers.get(event);
      if (handlers) {
        handlers.delete(handler);
      }
    };
  }

  // Remove event listener
  off(event, handler) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  // Emit event to all listeners
  emit(event, data) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`❌ [UNIFIED SOCKET] Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  // Add connection handler
  onConnect(handler) {
    this.connectionHandlers.add(handler);
    return () => this.connectionHandlers.delete(handler);
  }

  // Add disconnection handler
  onDisconnect(handler) {
    this.disconnectionHandlers.add(handler);
    return () => this.disconnectionHandlers.delete(handler);
  }

  // Add error handler
  onError(handler) {
    this.errorHandlers.add(handler);
    return () => this.errorHandlers.delete(handler);
  }

  // Handle disconnection
  handleDisconnection(platform, reason) {
    console.log(`🔌 [UNIFIED SOCKET] ${platform} disconnected:`, reason);
    
    this.disconnectionHandlers.forEach(handler => {
      try {
        handler({ platform, reason });
      } catch (error) {
        console.error('❌ [UNIFIED SOCKET] Error in disconnection handler:', error);
      }
    });

    // Check if both platforms are disconnected
    const hrmConnected = this.hrmSocket && this.hrmSocket.connected;
    const ticketingConnected = this.ticketingSocket && this.ticketingSocket.connected;
    
    if (!hrmConnected && !ticketingConnected) {
      this.isConnected = false;
    }
  }

  // Handle errors
  handleError(error) {
    console.error('❌ [UNIFIED SOCKET] Error:', error);
    
    this.errorHandlers.forEach(handler => {
      try {
        handler(error);
      } catch (err) {
        console.error('❌ [UNIFIED SOCKET] Error in error handler:', err);
      }
    });
  }

  // Start heartbeat
  startHeartbeat() {
    this.stopHeartbeat();
    
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected) {
        this.ping('hrm');
        this.ping('ticketing');
      }
    }, this.heartbeatInterval);
    
    console.log('💓 [UNIFIED SOCKET] Heartbeat started');
  }

  // Stop heartbeat
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
      console.log('💔 [UNIFIED SOCKET] Heartbeat stopped');
    }
  }

  // Get connection status
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      hrm: {
        connected: this.hrmSocket ? this.hrmSocket.connected : false,
        id: this.hrmSocket ? this.hrmSocket.id : null
      },
      ticketing: {
        connected: this.ticketingSocket ? this.ticketingSocket.connected : false,
        id: this.ticketingSocket ? this.ticketingSocket.id : null
      }
    };
  }

  // Get statistics
  getStats() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      eventHandlers: Array.from(this.eventHandlers.keys()),
      connectionHandlers: this.connectionHandlers.size,
      disconnectionHandlers: this.disconnectionHandlers.size,
      errorHandlers: this.errorHandlers.size
    };
  }
}

// Export singleton instance
export default new UnifiedSocketClient();
